import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { compact, debounce, isEqual } from 'lodash-es';
import { PaymentDetailType, ISSUES_PREVENTING_PAYMENT } from 'anchor-scout-common/types';
import { PageContentHeader, TableListProvider, TableListHeader, useMediaQueries, Spinner, PaginationControl, useOverlayContext } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import TableFilters from '~/components/TableFilters/TableFilters';
import { makeInitialFilterValue } from '~/components/TableFilters/TableFilters.utils';
import { ErrorText } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useUnmarkPaidExternally } from '~/hooks/invoice/useUnmarkPaidExternally';
import { useUnvoidInvoice } from '~/hooks/invoice/useUnvoidInvoice';
import { useInvoiceUpdatedEvent } from '~/hooks/pusher/useInvoiceUpdatedEvent';
import { useGetInvoice } from '~/hooks/reactQuery/queries/useInvoice';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { invoiceSummaryModel } from '~/models';
import { useSubscribeToBillingObservable } from '~/providers/BillingObservableProvider';
import { useSubscribeToInvoiceChangesObservable } from '~/providers/InvoiceChangesObservableProvider';
import { selectors } from '~/store/state-selectors';
import { useAppConfig } from '~/store/store-context';
import { getURLWithFullPageInvoice } from '../FullPageInvoice/FullPageInvoice.utils';
import { InvoiceMetricsSection } from '../InvoiceMetricsSection/InvoiceMetricsSection';
import { DEFAULT_TIME_PERIOD, serializeTimePeriod } from '../InvoiceMetricsSection/TimePeriodSelector/TimePeriodSelector';
import { NoFilteredInvoices } from '../InvoicesEmptyStates/InvoicesEmptyStates';
import { DateFilterPickerPresets } from './DateFilterPicker';
import styles from './InvoicesTable.module.scss';
import { applyOptimisticUpdate, getEffectiveTimePeriod, getInvoiceStatusFilterOptions, getIssuesPreventingPaymentFilterOptions, getProcessedByFilterOptions, getAccountingStatusFilterOptions, legacyDateChipToTimePeriod, mapSortAndFiltersRequestParams, getTableDefs } from './InvoicesTable.utils';
import { FilterType as InvoiceFilterType, getTableFilterLabels, formatFilterDescription, getActiveFilterGroups, getTableFilterConfig, invoiceFilterTextsPath } from './invoicesTableFilters';
import InvoicesTableRow from './InvoicesTableRow';
const defaultFilters = {
    period: serializeTimePeriod(DEFAULT_TIME_PERIOD),
    date: '',
    displayStatusV2: [],
    search: '',
    qbo: AccountingStatusFilter.All,
    xero: AccountingStatusFilter.All,
    paymentDetails: [],
    dueDateSince: '',
    dueDateUntil: ''
};
export function InvoicesTableWithData(props) {
    const { filtersClassname, filters, columnDefs, headerItems, sortOptions, hideDueDate, sort, loading, pageNumber, isEmptyFilters, showMetrics, onSortChange, onFilterChange, onResetFilters, retry, onPageNumberChange } = props;
    const { qboStatusFilterCounters, xeroStatusFilterCounters, total } = props.data;
    const { search, displayStatusV2: displayStatusV2Filter, period: periodFilter, date: legacyDateFilter, qbo: qboStatusFilter, xero: xeroStatusFilter, paymentDetails: paymentDetailsFilter, dueDateSince: dueDateSinceFilter, dueDateUntil: dueDateUntilFilter } = filters;
    const dueDateSinceMs = dueDateSinceFilter ? Number(dueDateSinceFilter) : undefined;
    const dueDateUntilMs = dueDateUntilFilter ? Number(dueDateUntilFilter) : undefined;
    const { paginationPageSize } = useAppConfig();
    const { AD_HOC_INVOICING, INVOICE_DASHBOARD_METRICS } = useFeatureFlags();
    const period = getEffectiveTimePeriod({
        period: periodFilter,
        date: legacyDateFilter
    }, INVOICE_DASHBOARD_METRICS);
    const { t: invoiceFilterTexts } = useLocaleKeys(invoiceFilterTextsPath);
    const { t: commonTexts } = useLocaleKeys('common');
    const [invoicesFromServer, setInvoicesFromServer] = useState(props.data.invoices);
    useEffect(()=>setInvoicesFromServer(props.data.invoices), [
        props.data.invoices
    ]);
    const { getNow } = useIoContext();
    const isVendor = useStore(selectors.user.isVendor);
    const { logBI, fetchIntegrationsSummary } = useStoreActions();
    const getInvoice = useGetInvoice();
    const queryClient = useQueryClient();
    const { startUnvoidInvoice, finishUnvoidInvoice } = useUnvoidInvoice();
    const { startUnmarkPaidExternally, finishUnmarkPaidExternally } = useUnmarkPaidExternally();
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11451);
    const invoiceStatusFilterOptions = getInvoiceStatusFilterOptions({
        counters: props.data.displayStatusV2FilterCounters || {},
        texts: invoiceFilterTexts.invoiceDisplayStatusLabels
    });
    const issuesPreventingPaymentFilterOptions = getIssuesPreventingPaymentFilterOptions({
        counters: props.data.paymentDetailsFilterCounters || {},
        texts: invoiceFilterTexts.paymentDetailLabels
    });
    const processedByFilterOptions = getProcessedByFilterOptions({
        counters: props.data.paymentDetailsFilterCounters || {},
        texts: invoiceFilterTexts.processedByLabels
    });
    const qboStatusFilterOptions = getAccountingStatusFilterOptions({
        counters: qboStatusFilterCounters,
        texts: invoiceFilterTexts.accountingStatusLabels
    });
    const xeroStatusFilterOptions = getAccountingStatusFilterOptions({
        counters: xeroStatusFilterCounters,
        texts: invoiceFilterTexts.accountingStatusLabels
    });
    const tableFilterConfig = getTableFilterConfig({
        invoiceStatusFilterOptions,
        issuesPreventingPaymentFilterOptions,
        processedByFilterOptions,
        qboStatusFilterOptions,
        xeroStatusFilterOptions
    });
    const activeFilterGroups = getActiveFilterGroups({
        showQboIntegration,
        showXeroIntegration,
        hasQboFilterOptions: qboStatusFilterOptions.length > 0,
        hasXeroFilterOptions: xeroStatusFilterOptions.length > 0,
        filterGroupLabels: invoiceFilterTexts.filterGroupLabels,
        isVendor,
        hideDateChip: INVOICE_DASHBOARD_METRICS
    });
    const [additionalFilters, setAdditionalFilters] = useState({});
    const tableSelectedFilters = useMemo(()=>{
        const next = {};
        for (const group of activeFilterGroups){
            for (const filter of group.filters){
                if (tableFilterConfig[filter].removable) continue;
                next[filter] = makeInitialFilterValue(tableFilterConfig[filter]);
            }
        }
        if (displayStatusV2Filter.length > 0) next[InvoiceFilterType.InvoiceStatus] = displayStatusV2Filter;
        if (!INVOICE_DASHBOARD_METRICS && legacyDateFilter && legacyDateFilter !== DateFilterPickerPresets.AllTime) {
            next[InvoiceFilterType.Date] = legacyDateFilter;
        }
        if (qboStatusFilter !== AccountingStatusFilter.All) next[InvoiceFilterType.QboSync] = qboStatusFilter;
        if (xeroStatusFilter !== AccountingStatusFilter.All) next[InvoiceFilterType.XeroSync] = xeroStatusFilter;
        const issueTypes = paymentDetailsFilter.filter((d)=>ISSUES_PREVENTING_PAYMENT.includes(d));
        const processedByTypes = paymentDetailsFilter.filter((d)=>d === PaymentDetailType.PaidOnDate || d === PaymentDetailType.PaidExternally).map((d)=>d === PaymentDetailType.PaidOnDate ? 'anchor' : 'external');
        if (issueTypes.length > 0) next[InvoiceFilterType.IssuesPreventingPayment] = issueTypes;
        if (processedByTypes.length > 0) next[InvoiceFilterType.ProcessedBy] = processedByTypes;
        return _object_spread({}, next, additionalFilters);
    }, [
        activeFilterGroups,
        tableFilterConfig,
        displayStatusV2Filter,
        legacyDateFilter,
        INVOICE_DASHBOARD_METRICS,
        qboStatusFilter,
        xeroStatusFilter,
        paymentDetailsFilter,
        additionalFilters
    ]);
    const handleTableFiltersRefresh = (newFilters, newSort)=>{
        var _newFilters_InvoiceFilterType_IssuesPreventingPayment, _newFilters_InvoiceFilterType_ProcessedBy, _newFilters_InvoiceFilterType_InvoiceStatus, _newFilters_InvoiceFilterType_QboSync, _newFilters_InvoiceFilterType_XeroSync;
        const issueTypes = (_newFilters_InvoiceFilterType_IssuesPreventingPayment = newFilters[InvoiceFilterType.IssuesPreventingPayment]) !== null && _newFilters_InvoiceFilterType_IssuesPreventingPayment !== void 0 ? _newFilters_InvoiceFilterType_IssuesPreventingPayment : [];
        const processedByKeys = (_newFilters_InvoiceFilterType_ProcessedBy = newFilters[InvoiceFilterType.ProcessedBy]) !== null && _newFilters_InvoiceFilterType_ProcessedBy !== void 0 ? _newFilters_InvoiceFilterType_ProcessedBy : [];
        const processedByTypes = processedByKeys.map((key)=>key === 'anchor' ? PaymentDetailType.PaidOnDate : PaymentDetailType.PaidExternally);
        const statusValues = (_newFilters_InvoiceFilterType_InvoiceStatus = newFilters[InvoiceFilterType.InvoiceStatus]) !== null && _newFilters_InvoiceFilterType_InvoiceStatus !== void 0 ? _newFilters_InvoiceFilterType_InvoiceStatus : [];
        const next = {
            displayStatusV2: statusValues,
            qbo: (_newFilters_InvoiceFilterType_QboSync = newFilters[InvoiceFilterType.QboSync]) !== null && _newFilters_InvoiceFilterType_QboSync !== void 0 ? _newFilters_InvoiceFilterType_QboSync : AccountingStatusFilter.All,
            xero: (_newFilters_InvoiceFilterType_XeroSync = newFilters[InvoiceFilterType.XeroSync]) !== null && _newFilters_InvoiceFilterType_XeroSync !== void 0 ? _newFilters_InvoiceFilterType_XeroSync : AccountingStatusFilter.All,
            paymentDetails: [
                ...issueTypes,
                ...processedByTypes
            ]
        };
        if (!INVOICE_DASHBOARD_METRICS) {
            var _newFilters_InvoiceFilterType_Date;
            next.date = (_newFilters_InvoiceFilterType_Date = newFilters[InvoiceFilterType.Date]) !== null && _newFilters_InvoiceFilterType_Date !== void 0 ? _newFilters_InvoiceFilterType_Date : DateFilterPickerPresets.AllTime;
        }
        onFilterChange(next);
        onSortChange(newSort);
    };
    const handlePeriodChange = (newPeriod)=>{
        if (INVOICE_DASHBOARD_METRICS) {
            setAdditionalFilters({});
            onFilterChange(_object_spread_props(_object_spread({}, defaultFilters), {
                period: serializeTimePeriod(newPeriod),
                date: ''
            }));
            return;
        }
        onFilterChange({
            period: serializeTimePeriod(newPeriod),
            date: ''
        });
    };
    useSubscribeToBillingObservable(()=>retry());
    useSubscribeToInvoiceChangesObservable(async (param)=>{
        let { optimisticUpdate } = param;
        if (optimisticUpdate) {
            setInvoicesFromServer((inv)=>applyOptimisticUpdate(inv, optimisticUpdate, getNow()));
            retry({
                disableFetchInvoices: true
            });
        } else {
            retry();
        }
    });
    useOnMount(()=>{
        if (showQboIntegration) {
            fetchIntegrationsSummary();
        }
    });
    const handleSortChange = (sort)=>{
        logBI(11450, {
            column: sort ? headerItems[sort.colIndex].id : 'none'
        });
        onSortChange(sort);
    };
    async function refetchAndApplyInvoice(invoiceId) {
        const updatedInvoice = await getInvoice(invoiceId);
        setInvoicesFromServer((invoices)=>invoices.map((i)=>i.id === invoiceId ? updatedInvoice : i));
        queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.InvoiceMetrics
            ]
        });
    }
    useInvoiceUpdatedEvent({
        onInvoiceRefetch: refetchAndApplyInvoice,
        onUnvoided: finishUnvoidInvoice,
        onUnmarkedAsPaidExternally: finishUnmarkPaidExternally
    });
    return /*#__PURE__*/ _jsxs(TableListProvider, {
        hasActionColumn: true,
        columnDefs: columnDefs,
        sort: sort,
        onSortChange: handleSortChange,
        children: [
            /*#__PURE__*/ _jsxs(PageContentHeader, {
                children: [
                    showMetrics && INVOICE_DASHBOARD_METRICS && /*#__PURE__*/ _jsx(InvoiceMetricsSection, {
                        period: period,
                        onPeriodChange: handlePeriodChange,
                        currentFilters: {
                            displayStatusV2: displayStatusV2Filter,
                            paymentDetails: paymentDetailsFilter,
                            search,
                            dueDateSince: dueDateSinceMs,
                            dueDateUntil: dueDateUntilMs,
                            qboIntegration: qboStatusFilter,
                            xeroIntegration: xeroStatusFilter
                        },
                        onApplyFilter: (filter)=>{
                            var _filter_paymentDetails;
                            setAdditionalFilters({});
                            onFilterChange(_object_spread_props(_object_spread({}, defaultFilters), {
                                period: serializeTimePeriod(period),
                                displayStatusV2: filter.displayStatusV2,
                                paymentDetails: (_filter_paymentDetails = filter.paymentDetails) !== null && _filter_paymentDetails !== void 0 ? _filter_paymentDetails : defaultFilters.paymentDetails,
                                dueDateSince: filter.dueDateSince != null ? String(filter.dueDateSince) : '',
                                dueDateUntil: filter.dueDateUntil != null ? String(filter.dueDateUntil) : ''
                            }));
                        }
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: filtersClassname,
                        children: /*#__PURE__*/ _jsx(TableFilters, {
                            disableFlexWrap: true,
                            testIdPrefix: "invoices-dashboard",
                            filterBarTestId: "invoices-dashboard-filter-picker-group",
                            search: {
                                bi: {
                                    name: 'invoices_search'
                                },
                                value: search,
                                placeholder: commonTexts.searchPlaceholder(),
                                testId: 'invoices-dashboard-search',
                                registerHotkey: true,
                                onChange: (event)=>{
                                    const search = event.target.value;
                                    onFilterChange({
                                        search
                                    }, {
                                        withDebounce: true
                                    });
                                    debouncedLogSearchToBI.onChange(search);
                                },
                                onClear: ()=>{
                                    onFilterChange({
                                        search: ''
                                    });
                                    debouncedLogSearchToBI.onClear();
                                }
                            },
                            filterLabels: getTableFilterLabels(invoiceFilterTexts.filterLabels),
                            filterConfig: tableFilterConfig,
                            filterGroups: activeFilterGroups,
                            selectedFilters: tableSelectedFilters,
                            setSelectedFilters: setAdditionalFilters,
                            onRefresh: handleTableFiltersRefresh,
                            onClear: INVOICE_DASHBOARD_METRICS && !isEmptyFilters ? ()=>{
                                setAdditionalFilters({});
                                onFilterChange(_object_spread_props(_object_spread({}, defaultFilters), {
                                    period: serializeTimePeriod(period)
                                }));
                            } : undefined,
                            isClearDisabled: !INVOICE_DASHBOARD_METRICS && isEmptyFilters,
                            modernClearButton: INVOICE_DASHBOARD_METRICS,
                            mobile: {
                                formatFilterDescription: (filter, value)=>formatFilterDescription({
                                        filterType: filter,
                                        value,
                                        texts: invoiceFilterTexts
                                    }),
                                sortOptions
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(TableListHeader, {
                        testId: "invoices-table-header",
                        items: headerItems
                    })
                ]
            }),
            loading ? /*#__PURE__*/ _jsx(Spinner, {
                centeredInBox: true,
                centerBoxHeight: 200
            }) : invoicesFromServer.length > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    invoicesFromServer.map((invoice, index)=>/*#__PURE__*/ _jsx(InvoicesTableRow, {
                            invoice: invoice,
                            index: index,
                            hideDueDate: hideDueDate,
                            showQboIntegration: showQboIntegration,
                            showXeroIntegration: showXeroIntegration,
                            AD_HOC_INVOICING: AD_HOC_INVOICING,
                            to: getURLWithFullPageInvoice({
                                invoiceId: invoice.id,
                                relationshipId: invoiceSummaryModel.relationshipId(invoice)
                            }),
                            retry: retry,
                            onUnvoidInvoice: ()=>startUnvoidInvoice(invoice),
                            onUnmarkPaidExternally: ()=>startUnmarkPaidExternally(invoice)
                        }, invoice.id)),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.paginationControls,
                        children: /*#__PURE__*/ _jsx(PaginationControl, {
                            compact: true,
                            pageSize: paginationPageSize,
                            pageNumber: pageNumber,
                            total: total,
                            onPageNumberChange: onPageNumberChange
                        })
                    })
                ]
            }) : /*#__PURE__*/ _jsx(NoFilteredInvoices, {
                resetFilters: onResetFilters
            })
        ]
    });
}
export default function InvoicesTable(props) {
    const { relationshipId, showMetrics = false, filtersClassname, onRetry, renderLoader, renderError, renderChild, renderNoInvoices } = props;
    const { getNow, browser: { downloadCsv } } = useIoContext();
    const { paginationPageSize, inputDebounceTime, exportCsvFilename } = useAppConfig();
    const [pageNumber, _setPageNumber] = useState(1);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const isVendor = useStore(selectors.user.isVendor);
    const { smallScreen } = useMediaQueries();
    const hideDueDate = smallScreen;
    const { api } = useStoreActions();
    const { showErrorToast } = useOverlayContext();
    const { t: invoiceTexts } = useLocaleKeys(invoiceFilterTextsPath);
    const { AD_HOC_INVOICING: AD_HOC_INVOICING_OUTER, EXTERNAL_INVOICES_ENDPOINT, INVOICE_DASHBOARD_METRICS } = useFeatureFlags();
    const queryClient = useQueryClient();
    const [exportingCsv, setExportingCsv] = useState(false);
    const defaultSort = useMemo(()=>({
            colIndex: 0,
            sort: 'desc'
        }), []);
    const { columnDefs, headerItems, sortIds, sortOptions } = useMemo(()=>getTableDefs({
            showQboIntegration,
            hideDueDate,
            showXeroIntegration,
            texts: invoiceTexts.columnHeaders,
            AD_HOC_INVOICING: AD_HOC_INVOICING_OUTER
        }), [
        showQboIntegration,
        hideDueDate,
        showXeroIntegration,
        AD_HOC_INVOICING_OUTER
    ]);
    const { sort, changeSort: _changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const { urlState, changeUrlState: _changeUrlState } = useStateFromUrl({
        displayStatusV2: {
            multi: true,
            defaultValue: defaultFilters.displayStatusV2
        },
        period: {
            defaultValue: defaultFilters.period
        },
        date: {
            defaultValue: defaultFilters.date
        },
        search: {
            defaultValue: defaultFilters.search
        },
        qbo: {
            defaultValue: defaultFilters.qbo
        },
        xero: {
            defaultValue: defaultFilters.xero
        },
        paymentDetails: {
            multi: true,
            defaultValue: defaultFilters.paymentDetails
        },
        dueDateSince: {
            defaultValue: defaultFilters.dueDateSince
        },
        dueDateUntil: {
            defaultValue: defaultFilters.dueDateUntil
        }
    });
    const urlStateRef = useRef(urlState);
    const sortRef = useRef(sort);
    const pageNumberRef = useRef(pageNumber);
    const { renderRestfulState, refresh } = useRestfulWrapper({
        fetch: ()=>{
            const params = _object_spread_props(_object_spread({}, mapSortAndFiltersRequestParams(_object_spread_props(_object_spread({}, urlStateRef.current), {
                period: INVOICE_DASHBOARD_METRICS ? getEffectiveTimePeriod({
                    period: urlStateRef.current.period,
                    date: urlStateRef.current.date
                }, true) : legacyDateChipToTimePeriod(urlStateRef.current.date, getNow()),
                sort: sortRef.current,
                now: getNow(),
                sortIds
            }))), {
                relationshipId,
                pageSize: paginationPageSize,
                pageNumber: pageNumberRef.current
            });
            return EXTERNAL_INVOICES_ENDPOINT ? api.invoice.getInvoices(params) : api.invoice.getAll(params);
        }
    });
    useOnMount(()=>{
        return ()=>refreshWithDebounce.cancel();
    });
    const refreshWithDebounce = useMemo(()=>{
        const debounceTime = inputDebounceTime != null ? inputDebounceTime : 500;
        return debounce(refresh, debounceTime);
    }, []);
    useEffect(()=>{
        const refIsStale = JSON.stringify(urlStateRef.current) !== JSON.stringify(urlState);
        if (refIsStale) {
            urlStateRef.current = urlState;
            refreshWithDebounce();
            refreshWithDebounce.flush();
        }
    }, [
        urlState,
        refreshWithDebounce
    ]);
    function changeUrlState(newUrlState) {
        _changeUrlState(newUrlState);
        urlStateRef.current = _object_spread({}, urlStateRef.current, newUrlState);
    }
    function changeSort(newSort) {
        _changeSort(newSort);
        sortRef.current = newSort;
    }
    function setPageNumber(pageNumber) {
        _setPageNumber(pageNumber);
        pageNumberRef.current = pageNumber;
    }
    function handleFilterChange(newUrlState, opts) {
        changeUrlState(newUrlState);
        setPageNumber(1);
        scheduleRefresh(opts);
    }
    function handleSortChange(sort) {
        changeSort(sort);
        setPageNumber(1);
        scheduleRefresh();
    }
    function handlePageNumberChange(pageNumber) {
        setPageNumber(pageNumber);
        scheduleRefresh();
    }
    function scheduleRefresh() {
        let { withDebounce } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        refreshWithDebounce();
        !withDebounce && refreshWithDebounce.flush();
    }
    return renderRestfulState({
        renderLoader,
        renderError,
        renderChild: (param)=>{
            let { data, loading, refresh } = param;
            // When the metrics flag is on the segment is the date control and is excluded from
            // \"is the user filtering\" semantics — the dashboard should still render normally
            // (and the Clear button should stay disabled) when only the segment is non-default.
            // When the flag is off the legacy \`date\` chip IS a user-applied filter, so it
            // contributes to the empty-state and clear-button decisions as before. Normalize
            // \\\`'all-time'\\\` to \\\`''\\\` (both mean \"no date filter\") so isEmptyFilters reports
            // true after the user explicitly picks \"All time\" — matching pre-PR behavior.
            const normalizedDateForCompare = urlState.date === DateFilterPickerPresets.AllTime ? '' : urlState.date;
            const isEmptyFilters = INVOICE_DASHBOARD_METRICS ? isEqual(_object_spread_props(_object_spread({}, urlState), {
                period: defaultFilters.period,
                date: defaultFilters.date
            }), defaultFilters) : isEqual(_object_spread_props(_object_spread({}, urlState), {
                date: normalizedDateForCompare
            }), defaultFilters);
            // The empty-state branch is gated on a stricter check that DOES include period/date.
            // Otherwise, picking a non-default time period (e.g. \"Specific range\") that returns
            // zero invoices renders the brand-new-vendor welcome screen instead of the
            // \"no invoices match these filters\" state.
            const isFullyDefaultFilters = isEqual(_object_spread_props(_object_spread({}, urlState), {
                date: normalizedDateForCompare
            }), defaultFilters);
            if (!data.invoices.length && !loading && renderNoInvoices && isFullyDefaultFilters) {
                return renderNoInvoices();
            }
            const tableContent = /*#__PURE__*/ _jsx(InvoicesTableWithData, {
                data: data,
                columnDefs: columnDefs,
                headerItems: headerItems,
                sortOptions: sortOptions,
                hideDueDate: hideDueDate,
                sort: sort,
                filtersClassname: filtersClassname,
                filters: urlState,
                isEmptyFilters: isEmptyFilters,
                loading: loading,
                pageNumber: pageNumber,
                showMetrics: showMetrics,
                onSortChange: handleSortChange,
                onFilterChange: handleFilterChange,
                onResetFilters: ()=>handleFilterChange(defaultFilters),
                onPageNumberChange: handlePageNumberChange,
                retry: async function retry() {
                    let { disableFetchInvoices } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                    await Promise.all(compact([
                        !disableFetchInvoices && refresh(),
                        onRetry && onRetry()
                    ]));
                    queryClient.invalidateQueries({
                        queryKey: [
                            QueryKeys.InvoiceMetrics
                        ]
                    });
                }
            });
            return renderChild ? renderChild({
                data,
                loading,
                tableContent,
                exportingCsv,
                refresh,
                getRows: ()=>data.invoices,
                async exportCsv () {
                    setExportingCsv(true);
                    const params = mapSortAndFiltersRequestParams(_object_spread_props(_object_spread({}, urlStateRef.current), {
                        period: INVOICE_DASHBOARD_METRICS ? getEffectiveTimePeriod({
                            period: urlStateRef.current.period,
                            date: urlStateRef.current.date
                        }, true) : legacyDateChipToTimePeriod(urlStateRef.current.date, getNow()),
                        sort: sortRef.current,
                        sortIds,
                        now: getNow()
                    }));
                    const result = await handlePromise(api.invoice.exportToCsvV2(_object_spread_props(_object_spread({}, params), {
                        view: isVendor ? 'vendor' : 'customer'
                    })));
                    if (result.success) {
                        const filename = exportCsvFilename || 'exported-invoices.csv';
                        downloadCsv(result.data, filename);
                    } else {
                        showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'export-invoices-error');
                    }
                    setExportingCsv(false);
                }
            }) : tableContent;
        }
    });
}
